import { ComponentType, createContext, useContext } from 'react';

import { useLineChartContext } from './context';
import { LineChartAnnotation, LineChartData } from './types';

export const TooltipContext = createContext<TooltipData | null>(null);

import css from './styles.module.css';

export interface TooltipData {
  index: number;
  annotation?: {
    seriesKey: string;
    index: number;
  };
}

export interface LineChartDataTooltipRenderProps<Data extends LineChartData> {
  data: Data[];
  index: number;
}

export interface DataTooltipProps<Data extends LineChartData> {
  render: ComponentType<LineChartDataTooltipRenderProps<Data>>;
}

export function DataTooltip<Data extends LineChartData>({
  render: Component,
}: DataTooltipProps<Data>) {
  const tooltip = useContext(TooltipContext);
  if (!tooltip) return null;

  const context = useLineChartContext<Data>();

  const dataPts = context.series.flatMap((s) => s.data.at(tooltip.index) ?? []);
  if (dataPts.length !== context.series.length) return null;

  return (
    <div className={css.tooltip} data-type="data">
      <Component data={dataPts} index={tooltip.index} />
    </div>
  );
}

export interface LineChartAnnotationTooltipRenderProps<
  Annotation extends LineChartAnnotation,
> {
  data: Annotation;
  seriesKey: string;
  index: number;
}

export interface AnnotationTooltipProps<
  Annotation extends LineChartAnnotation,
> {
  render: ComponentType<LineChartAnnotationTooltipRenderProps<Annotation>>;
}

export function AnnotationTooltip<Annotation extends LineChartAnnotation>({
  render: Component,
}: AnnotationTooltipProps<Annotation>) {
  const tooltip = useContext(TooltipContext);
  if (!tooltip?.annotation) return null;

  const context = useLineChartContext<LineChartData, Annotation>();
  const seriesKey = tooltip.annotation?.seriesKey;
  const match = context.series.find((s) => s.key === seriesKey);
  const annotation = match?.annotations?.at(tooltip.annotation?.index ?? 0);

  if (!annotation) return null;

  return (
    <div className={css.tooltip} data-type="annotation">
      <Component
        data={annotation}
        seriesKey={tooltip.annotation.seriesKey}
        index={tooltip.annotation.index}
      />
    </div>
  );
}
